// Spaced-repetition pool model. Pure functions over the progress object `S` — no DOM.
//
// A deck's events are sorted by familiarity (easiest first) and cut into subsets of SUBSET. The *pool* is the
// first `pool` subsets. Each event sits in a Leitner box; a success moves it up and pushes its
// next review further out (INTERVALS, counted in ROUNDS of that deck — never in days, so replaying
// back to back works), a miss sends it back to box 0. When the pool is learned
// well enough, the next subset joins it automatically.
export const SUBSET = 10;
export const ROUND = 10;
export const OK = 70;                       // points that count as a success
export const INTERVALS = [0, 1, 2, 4, 8, 16, 32, 64]; // rounds until next review, indexed by box
export const MASTERED_BOX = 4;              // four spaced successes (interval >= 8 rounds)
export const GROW_RATE = 0.85;              // pool success rate that admits the next subset
export const GROW_MIN_SEEN = 1;             // every pool event answered at least this often
export const HISTORY = 3;                   // recent answers per event (used for the rate until a deck has its own window)
export const WINDOW = 20;                   // the pool's success rate is measured over its last WINDOW answers
export const EASE_RATE = 0.7;               // ...and if every event has been seen and the rate is at least this
export const EASE_ROUNDS = 3;               // ...the next subset opens anyway after this many rounds without growth

// Teaching order: by `rank` (most familiar first, hardest last); chronological when no rank is set.
export const sortEvents = (evs) => [...evs].sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0) || a.start - b.start || a.end - b.end || (a.id < b.id ? -1 : 1));
export const subsetCount = (evs) => Math.ceil(evs.length / SUBSET);
export const subsetOf = (evsSorted, i) => evsSorted.slice(i * SUBSET, (i + 1) * SUBSET);

export const deckRec = (S, id) => { const d = S.decks[id] || (S.decks[id] = { pool: 1, log: [] }); if (d.clock == null) d.clock = 0; return d; };
/** One finished round in this deck. */
export const tick = (S, id) => { const d = deckRec(S, id); d.clock++; d.sinceGrow = (d.sinceGrow || 0) + 1; };
export const poolEvents = (S, id, evsSorted) => evsSorted.slice(0, deckRec(S, id).pool * SUBSET);
export const isMastered = (S, id) => (S.events[id]?.box ?? -1) >= MASTERED_BOX;
export const isDue = (S, id, clock) => { const r = S.events[id]; return !!r && r.due <= clock; };

/** Apply one answer at the deck's current `clock`. Early successes (before the review is due) don't advance the box, so replaying can't be farmed. */
export function record(S, id, points, clock = 0, deck) {
  const ok = points >= OK;
  const r = S.events[id] || (S.events[id] = { box: 0, due: 0, n: 0, h: [] });
  const fresh = r.n === 0;
  if (!ok) { r.box = 0; r.due = clock; }
  else if (fresh) { r.box = 1; r.due = clock + INTERVALS[1]; }
  else if (r.due <= clock) { r.box = Math.min(r.box + 1, INTERVALS.length - 1); r.due = clock + INTERVALS[r.box]; }
  r.n++; r.t = Date.now();
  r.h = [...(r.h || []), ok ? 1 : 0].slice(-HISTORY);
  if (deck) { const d = deckRec(S, deck); d.recent = [...(d.recent || []), ok ? 1 : 0].slice(-WINDOW); }
  return ok;
}

/** Choose the next round: due items first (capped so new ones still appear), then new, then the soonest-due rest. */
export function pickRound(S, id, evsSorted, size = ROUND) {
  const now = deckRec(S, id).clock;
  const pool = poolEvents(S, id, evsSorted);
  const seen = pool.filter((e) => S.events[e.id]);
  const due = seen.filter((e) => S.events[e.id].due <= now).sort((a, b) => S.events[a.id].due - S.events[b.id].due);
  const fresh = pool.filter((e) => !S.events[e.id]);
  const rest = seen.filter((e) => S.events[e.id].due > now).sort((a, b) => S.events[a.id].due - S.events[b.id].due);
  const out = [];
  const take = (arr, n) => { for (const e of arr) { if (out.length >= size || n-- <= 0) break; if (!out.includes(e)) out.push(e); } };
  take(due, fresh.length ? Math.ceil(size * 0.6) : size);
  take(fresh, size);
  take(due, size);
  take(rest, size);
  return out;
}

export function poolStats(S, id, evsSorted) {
  const d = deckRec(S, id), now = d.clock, pool = poolEvents(S, id, evsSorted);
  // Rate over the deck's last WINDOW answers; for older saves without a window, over each event's last few answers.
  const evAnswers = pool.flatMap((e) => S.events[e.id]?.h || []);
  const rec = (d.recent || []).length >= Math.min(10, pool.length) ? d.recent : evAnswers;
  const good = rec.reduce((a, b) => a + b, 0);
  const fresh = pool.filter((e) => !S.events[e.id]).length;
  const rate = rec.length ? good / rec.length : 0;
  return {
    pool: d.pool, subsets: subsetCount(evsSorted), poolSize: pool.length, total: evsSorted.length,
    seen: pool.length - fresh,
    mastered: evsSorted.filter((e) => isMastered(S, e.id)).length,
    due: pool.filter((e) => isDue(S, e.id, now)).length,
    fresh, good, answers: rec.length, rate,
    ready: pool.every((e) => (S.events[e.id]?.n || 0) >= GROW_MIN_SEEN),
    sinceGrow: d.sinceGrow || 0,
    easeIn: Math.max(0, EASE_ROUNDS - (d.sinceGrow || 0)),   // rounds until the "good enough" rule applies
  };
}

/** Admit the next subset if the pool is learned well enough. Returns the log entry, or null. */
export function maybeGrow(S, id, evsSorted) {
  const now = Date.now(), d = deckRec(S, id), st = poolStats(S, id, evsSorted);
  if (d.pool >= st.subsets || !st.ready) return null;
  const strong = st.rate >= GROW_RATE, eased = !strong && st.rate >= EASE_RATE && st.sinceGrow >= EASE_ROUNDS;
  if (!strong && !eased) return null;
  d.pool++; d.sinceGrow = 0;
  const entry = { at: now, type: 'subset', subset: d.pool, events: d.pool * SUBSET > st.total ? st.total : d.pool * SUBSET, good: st.good, answers: st.answers };
  if (eased) entry.eased = true;
  d.log.push(entry);
  return entry;
}
